/** Usuarios: buscar (email, @usuario, uid) y ficha con acciones (baneo, rol, PRO por admin, mensajería, vacaciones). */
import { useEffect, useState, type ReactElement } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { api } from '@nuvvu/shared/api';
import type { AdminUserRow } from '@nuvvu/shared/api';
import { PRO_TIERS, type ProTier } from '@nuvvu/shared';
import { call, errorText } from '../api.js';
import { db } from '../firebase.js';
import { env } from '../env.js';
import { btn, card, field, muted, Notice, Table, td, when, money, str as sv } from '../lib/ui.js';

type Doc = Record<string, unknown>;

const FILTERS: Record<string, string> = {
  todos: 'Últimos registrados',
  nuevos: 'Nuevos (30 días)',
  pro: 'PRO',
  baneados: 'Baneados',
  bloqueos: 'Bloqueos entre usuarios',
  profesionales: 'Profesionales pendientes',
};
const TIER_NAME: Record<string, string> = {
  elite: 'PRO Absoluto',
  business: 'PRO Max',
  plus: 'PRO Plus',
  basic: 'PRO',
};
const tsMs = (v: unknown): number =>
  v && typeof v === 'object' && 'toMillis' in v ? (v as { toMillis: () => number }).toMillis() : 0;

/** Listas del Panel (27/10): `?filtro=todos|nuevos|pro|baneados|bloqueos` (+ `&nivel=` en PRO). */
function UserLists({ filtro, nivel }: { filtro: string; nivel: string | null }): ReactElement {
  const [rows, setRows] = useState<Doc[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setRows(null);
    setError(null);
    const users = collection(db, 'users');
    const load = async (): Promise<Doc[]> => {
      if (filtro === 'bloqueos') {
        const s = await getDocs(query(collection(db, 'userBlocks'), limit(300)));
        return s.docs
          .map((d): Doc => ({ id: d.id, ...d.data() }))
          .sort((a, b) => tsMs(b.created_at) - tsMs(a.created_at));
      }
      const q =
        filtro === 'pro'
          ? query(users, where('is_pro', '==', true), limit(500))
          : filtro === 'baneados'
            ? query(users, where('banned', '==', true), limit(500))
            : filtro === 'profesionales'
              ? query(users, where('trader_public.status', '==', 'pending'), limit(500))
              : filtro === 'nuevos'
                ? query(
                    users,
                    where('created_at', '>=', new Date(Date.now() - 30 * 86_400_000)),
                    orderBy('created_at', 'desc'),
                    limit(500),
                  )
                : query(users, orderBy('created_at', 'desc'), limit(100));
      const list = (await getDocs(q)).docs.map((d) => ({ id: d.id, ...d.data() }));
      if (filtro !== 'pro') return list;
      const withTier = await Promise.all(
        list.map(async (u) => {
          const t: unknown = (await getDoc(doc(db, `users/${u.id}/private/proStatus`))).data()
            ?.tier;
          return { ...u, tier: typeof t === 'string' ? t : 'basic' };
        }),
      );
      return nivel ? withTier.filter((u) => u.tier === nivel) : withTier;
    };
    load()
      .then(setRows)
      .catch((e: unknown) => {
        setError(errorText(e));
        setRows([]);
      });
  }, [filtro, nivel]);
  const title = `${FILTERS[filtro] ?? filtro}${nivel ? ` · ${TIER_NAME[nivel] ?? nivel}` : ''}`;
  return (
    <section style={{ display: 'grid', gap: 8 }}>
      <h2 style={{ margin: 0, fontSize: 16 }}>
        {title} {rows ? `(${String(rows.length)})` : ''}
      </h2>
      <Notice text={error} error />
      {!rows && <p style={muted}>Cargando…</p>}
      {rows && filtro === 'bloqueos' && (
        <Table head={['Bloquea', 'Bloqueado', 'Fecha']}>
          {rows.map((b) => (
            <tr key={sv(b.id)}>
              <td style={td}>
                <Link to={`/usuarios/${sv(b.blocker_id)}`}>{sv(b.blocker_id)}</Link>
              </td>
              <td style={td}>
                <Link to={`/usuarios/${sv(b.blocked_id)}`}>{sv(b.blocked_id)}</Link>
              </td>
              <td style={td}>{when(b.created_at)}</td>
            </tr>
          ))}
        </Table>
      )}
      {rows && filtro !== 'bloqueos' && (
        <Table
          head={['Usuario', 'Email', 'País', filtro === 'pro' ? 'Nivel' : 'PRO', 'Baneado', 'Alta']}
        >
          {rows.map((u) => (
            <tr key={sv(u.id)}>
              <td style={td}>
                <Link to={`/usuarios/${sv(u.id)}`}>
                  {typeof u.username === 'string' && u.username ? `@${u.username}` : sv(u.id)}
                </Link>
              </td>
              <td style={td}>{sv(u.email) || '—'}</td>
              <td style={td}>{sv(u.country_code) || '—'}</td>
              <td style={td}>
                {filtro === 'pro'
                  ? (TIER_NAME[sv(u.tier)] ?? sv(u.tier))
                  : u.is_pro === true
                    ? 'sí'
                    : '—'}
              </td>
              <td style={td}>{u.banned === true ? 'sí' : '—'}</td>
              <td style={td}>{when(u.created_at)}</td>
            </tr>
          ))}
        </Table>
      )}
    </section>
  );
}

export function UsersPage(): ReactElement {
  const [params, setParams] = useSearchParams();
  const filtro = params.get('filtro');
  const nivel = params.get('nivel');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<AdminUserRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const search = (): void => {
    setError(null);
    call(api.admin.searchUsers, { q })
      .then((r) => {
        setRows(r.users);
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };
  return (
    <>
      <h1 style={{ margin: 0 }}>Usuarios</h1>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {Object.entries(FILTERS).map(([k, label]) => (
          <button
            key={k}
            type="button"
            style={btn(filtro === k && !nivel)}
            onClick={() => {
              setParams({ filtro: k }, { replace: true });
            }}
          >
            {label}
          </button>
        ))}
        {Object.entries(TIER_NAME).map(([k, label]) => (
          <button
            key={k}
            type="button"
            style={btn(filtro === 'pro' && nivel === k)}
            onClick={() => {
              setParams({ filtro: 'pro', nivel: k }, { replace: true });
            }}
          >
            {label}
          </button>
        ))}
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          search();
        }}
        style={{ display: 'flex', gap: 8 }}
      >
        <input
          style={field}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
          }}
          placeholder="email, @usuario o uid"
        />
        <button type="submit" style={btn(true)}>
          Buscar
        </button>
      </form>
      <Notice text={error} error />
      {rows && (
        <Table head={['Usuario', 'Email', 'País', 'Rol', 'PRO', 'Baneado', 'Alta']}>
          {rows.length === 0 && (
            <tr>
              <td style={td} colSpan={7}>
                Sin resultados
              </td>
            </tr>
          )}
          {rows.map((u) => (
            <tr key={u.uid}>
              <td style={td}>
                <Link to={`/usuarios/${u.uid}`}>{u.username ? `@${u.username}` : u.uid}</Link>
              </td>
              <td style={td}>{u.email ?? '—'}</td>
              <td style={td}>{u.country_code ?? '—'}</td>
              <td style={td}>{u.role}</td>
              <td style={td}>{u.is_pro ? 'sí' : '—'}</td>
              <td style={td}>{u.banned ? 'sí' : '—'}</td>
              <td style={td}>{u.created_at ? when(u.created_at) : '—'}</td>
            </tr>
          ))}
        </Table>
      )}
      {filtro && <UserLists filtro={filtro} nivel={nivel} />}
    </>
  );
}

export function UserPage(): ReactElement {
  const { uid = '' } = useParams();
  const [u, setU] = useState<Doc | null | undefined>(undefined);
  const [pro, setPro] = useState<Doc | null>(null);
  const [wallet, setWallet] = useState<Doc | null>(null);
  const [orders, setOrders] = useState<Doc[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [proTier, setProTier] = useState<ProTier>('basic');
  const [proDays, setProDays] = useState('30');
  const [role, setRole] = useState<'user' | 'staff' | 'admin'>('user');
  const [version, setVersion] = useState(0);

  useEffect(() => {
    getDoc(doc(db, 'users', uid))
      .then((s) => {
        setU(s.exists() ? s.data() : null);
        const r = s.get('role') as string | undefined;
        if (r === 'admin' || r === 'staff' || r === 'user') setRole(r);
      })
      .catch(() => {
        setU(null);
      });
    getDoc(doc(db, `users/${uid}/private/proStatus`))
      .then((s) => {
        setPro(s.exists() ? s.data() : null);
      })
      .catch(() => undefined);
    getDoc(doc(db, 'wallets', uid))
      .then((s) => {
        setWallet(s.exists() ? s.data() : null);
      })
      .catch(() => undefined);
    getDocs(
      query(
        collection(db, 'orders'),
        where('buyer_id', '==', uid),
        orderBy('created_at', 'desc'),
        limit(10),
      ),
    )
      .then((s) => {
        setOrders(s.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch(() => undefined);
  }, [uid, version]);

  const act = (fn: () => Promise<unknown>, ok: string): void => {
    setError(null);
    setNotice(null);
    fn()
      .then(() => {
        setNotice(ok);
        setVersion((v) => v + 1);
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };

  if (u === undefined) return <p>Cargando…</p>;
  if (u === null) return <p>No existe el usuario {uid}.</p>;
  const str = (k: string): string => (typeof u[k] === 'string' ? u[k] : '—');
  return (
    <>
      <h1 style={{ margin: 0 }}>
        {typeof u.username === 'string' ? `@${u.username}` : uid}{' '}
        <span style={muted}>{str('email')}</span>
      </h1>
      <Notice text={notice} />
      <Notice text={error} error />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 12,
        }}
      >
        <section style={card}>
          <strong>Datos</strong>
          <div style={{ fontSize: 13, display: 'grid', gap: 4 }}>
            <span>
              uid: <code>{uid}</code>
            </span>
            <span>Nombre: {str('full_name')}</span>
            <span>
              País: {str('country_code')} · Idioma: {str('language')} · Moneda: {str('currency')}
            </span>
            <span>
              Rol: {str('role')} · PRO: {u.is_pro === true ? 'sí' : 'no'} · Baneado:{' '}
              {u.banned === true ? 'sí' : 'no'} · Vacaciones:{' '}
              {u.vacation_mode === true ? 'sí' : 'no'}
            </span>
            <span>
              Email verificado: {u.email_verified === true ? 'sí' : 'no'} · Teléfono: {str('phone')}{' '}
              · KYC: {u.kyc_verified === true ? 'sí' : 'no'}
            </span>
            <span>
              Nivel vendedor: {str('seller_level')} · Compras completadas:{' '}
              {sv(u.completed_purchases_count) || '0'}
            </span>
            <span>
              Alta: {when(u.created_at)} · Último acceso: {when(u.last_seen_at)}
            </span>
            <span>Mensajería restringida hasta: {when(u.messaging_restricted_until) || '—'}</span>
            <span>Stripe customer: {str('stripe_customer_id')}</span>
          </div>
        </section>
        <section style={card}>
          <strong>Wallet y PRO</strong>
          <div style={{ fontSize: 13, display: 'grid', gap: 4 }}>
            <span>
              Disponible: {money(wallet?.available) || '—'} · Retenido: {money(wallet?.held) || '—'}{' '}
              · Pendiente: {money(wallet?.pending) || '—'}
            </span>
            <span>
              PRO:{' '}
              {pro?.tier
                ? `${sv(pro.tier)} (${sv(pro.source)}) hasta ${when(pro.expires_at)}`
                : '—'}
            </span>
          </div>
        </section>
        <section style={card}>
          <strong>Acciones</strong>
          <input
            style={field}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            placeholder="Motivo (va al auditLog)"
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Link to={`/correo?uid=${uid}`} style={{ ...btn(false), textDecoration: 'none' }}>
              Enviar correo
            </Link>
            <button
              type="button"
              style={btn(false)}
              title="Abre la app en una pestaña nueva con una sesión de solo lectura como este usuario (1 h). Queda en el auditLog."
              onClick={() => {
                impersonate(uid, reason, setError, setNotice);
              }}
            >
              Ver como el usuario
            </button>
            <button
              type="button"
              style={btn(false, u.banned !== true)}
              onClick={() => {
                if (
                  window.confirm(
                    u.banned === true
                      ? '¿Levantar el baneo?'
                      : '¿Banear la cuenta? Se cierran sus sesiones.',
                  )
                )
                  act(
                    () => call(api.admin.updateUser, { uid, banned: u.banned !== true, reason }),
                    'Hecho.',
                  );
              }}
            >
              {u.banned === true ? 'Levantar baneo' : 'Banear'}
            </button>
            <button
              type="button"
              style={btn(false)}
              onClick={() => {
                act(
                  () => call(api.admin.updateUser, { uid, messaging_restricted_days: 7, reason }),
                  'Mensajería restringida 7 días.',
                );
              }}
            >
              Restringir mensajería 7 días
            </button>
            <button
              type="button"
              style={btn(false)}
              onClick={() => {
                act(
                  () => call(api.admin.updateUser, { uid, messaging_restricted_days: 0, reason }),
                  'Mensajería liberada.',
                );
              }}
            >
              Liberar mensajería
            </button>
            <button
              type="button"
              style={btn(false)}
              onClick={() => {
                act(
                  () =>
                    call(api.admin.updateUser, {
                      uid,
                      vacation_mode: u.vacation_mode !== true,
                      reason,
                    }),
                  'Hecho.',
                );
              }}
            >
              {u.vacation_mode === true ? 'Quitar vacaciones' : 'Poner en vacaciones'}
            </button>
          </div>
          {u.seller_type === 'trader' && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={muted}>
                Profesional: {sv((u.trader_public as Doc | undefined)?.status)} ·{' '}
                {sv((u.trader_public as Doc | undefined)?.legal_name)} ·{' '}
                {sv((u.trader_public as Doc | undefined)?.address)} · VAT{' '}
                {sv((u.trader_public as Doc | undefined)?.vat_number) || '-'}
              </span>
              {(['verified', 'rejected', 'suspended'] as const).map((st) => (
                <button
                  key={st}
                  type="button"
                  // 2/11: el estado actual no se puede volver a pulsar (antes parecía que no hacía nada).
                  disabled={sv((u.trader_public as Doc | undefined)?.status) === st}
                  style={{
                    ...btn(st === 'verified'),
                    ...(sv((u.trader_public as Doc | undefined)?.status) === st
                      ? { opacity: 0.5, cursor: 'default' }
                      : {}),
                  }}
                  onClick={() => {
                    act(
                      () => call(api.admin.updateUser, { uid, trader_status: st, reason }),
                      'Hecho.',
                    );
                  }}
                >
                  {sv((u.trader_public as Doc | undefined)?.status) === st
                    ? st === 'verified'
                      ? '✓ Verificado'
                      : st === 'rejected'
                        ? '✓ Rechazado'
                        : '✓ Suspendido'
                    : st === 'verified'
                      ? 'Verificar profesional'
                      : st === 'rejected'
                        ? 'Rechazar'
                        : 'Suspender'}
                </button>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              style={{ ...field, width: 'auto' }}
              value={proTier}
              onChange={(e) => {
                setProTier(e.target.value as ProTier);
              }}
            >
              {PRO_TIERS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <input
              style={{ ...field, width: 90 }}
              type="number"
              min={1}
              value={proDays}
              onChange={(e) => {
                setProDays(e.target.value);
              }}
            />
            <span style={muted}>días</span>
            <button
              type="button"
              style={btn(true)}
              onClick={() => {
                act(
                  () =>
                    call(api.admin.updateUser, {
                      uid,
                      pro: { tier: proTier, days: Number(proDays) || 30 },
                      reason,
                    }),
                  'PRO concedido.',
                );
              }}
            >
              Conceder PRO (admin)
            </button>
            <button
              type="button"
              style={btn(false)}
              onClick={() => {
                act(
                  () => call(api.admin.updateUser, { uid, pro: null, reason }),
                  'PRO de admin retirado.',
                );
              }}
            >
              Retirar PRO de admin
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              style={{ ...field, width: 'auto' }}
              value={role}
              onChange={(e) => {
                setRole(e.target.value as 'user' | 'staff' | 'admin');
              }}
            >
              <option value="user">user</option>
              <option value="staff">staff</option>
              <option value="admin">admin</option>
            </select>
            <button
              type="button"
              style={btn(false)}
              onClick={() => {
                if (window.confirm(`¿Cambiar el rol a ${role}?`))
                  act(
                    () => call(api.admin.grantRole, { uid, role, ...(reason ? { reason } : {}) }),
                    'Rol cambiado (tiene que volver a entrar).',
                  );
              }}
            >
              Cambiar rol
            </button>
          </div>
        </section>
      </div>
      <section style={card}>
        <strong>Últimas compras</strong>
        <Table head={['Pedido', 'Estado', 'Total', 'Fecha']}>
          {orders.map((o) => (
            <tr key={String(o.id)}>
              <td style={td}>
                <Link to={`/pedidos/${String(o.id)}`}>{String(o.id).slice(-6).toUpperCase()}</Link>
              </td>
              <td style={td}>{String(o.status)}</td>
              <td style={td}>{money((o.amounts as Doc | undefined)?.total)}</td>
              <td style={td}>{when(o.created_at)}</td>
            </tr>
          ))}
        </Table>
      </section>
    </>
  );
}

/**
 * Impersonación (1.0): admin-startImpersonation devuelve un custom token del usuario (claims de
 * solo lectura). Se abre la web en `/:lang/impersonate` y, cuando avisa que está lista, se le
 * manda el token por postMessage al origen de la web (nunca por la URL). El panel conserva su
 * propia sesión.
 */
function impersonate(
  uid: string,
  reason: string,
  setError: (e: string | null) => void,
  setNotice: (n: string | null) => void,
): void {
  const win = window.open(`${env.VITE_WEB_ORIGIN}/es/impersonate`, '_blank');
  if (!win) {
    setError('El navegador ha bloqueado la pestaña nueva.');
    return;
  }
  call(api.admin.startImpersonation, { uid, ...(reason ? { reason } : {}) })
    .then((r) => {
      const onMessage = (ev: MessageEvent<unknown>): void => {
        if (ev.origin !== env.VITE_WEB_ORIGIN || ev.source !== win) return;
        const d = ev.data as { type?: unknown } | null;
        if (d?.type !== 'nuvvu:impersonate-ready') return;
        win.postMessage(
          {
            type: 'nuvvu:impersonate',
            token: r.token,
            session_id: r.session_id,
            expires_at: r.expires_at,
            target: r.target,
          },
          env.VITE_WEB_ORIGIN,
        );
        window.removeEventListener('message', onMessage);
        setNotice(
          `Sesión de solo lectura como ${r.target.username ?? uid} abierta en otra pestaña (1 h).`,
        );
      };
      window.addEventListener('message', onMessage);
      // Por si la pestaña ya avisó antes de tener el token: se le pide que repita.
      win.postMessage({ type: 'nuvvu:impersonate-ping' }, env.VITE_WEB_ORIGIN);
    })
    .catch((e: unknown) => {
      win.close();
      setError(errorText(e));
    });
}
